import request from '@/utils/request'
import { buildErpDocumentsUrl, buildErpDocumentSummariesUrl, CONFIRM_STOCK_TRANSFER_URL, ERP_DOCUMENTS_DELETE_URL, ERP_DOCUMENTS_SAVE_URL, REMOVE_LOGS_URL } from './endpoints'
import { toRepeatQuery } from './queryString'
import {
  ConfirmStockTransferReq,
  DeleteErpDocumentsReq,
  DeleteErpDocumentsRes,
  DocumentRemoveLog,
  ErpDocumentItem,
  ErpDocumentSummary,
  GetErpDocumentsReq,
  GetGoodsCodeOptionsReq,
  GetStyleNumberOptionsReq,
  GoodsCodeOption,
  SaveErpDocumentsReq,
  SaveErpDocumentsRes,
  StyleNumberOptionItem,
} from './types'

export { ERP_DOCUMENT_SUMMARIES_EXCEL_URL, buildErpDocumentSummariesExcelUrl } from './endpoints'

/**
 * 보조 조회(서치헬퍼) 엔드포인트 — ST10/ST11 과 동일 배선:
 * - 상품코드 서치헬퍼 `GET /api/v1/master/products/goods-codes` — LOGISPRD-9955 (2.0)
 * - 스타일넘버 서치헬퍼 `GET /api/v1/master/products/style-numbers` — LOGISPRD-14184 (2.0, 1.0 grid/search STL_NO 대체)
 */
export const GOODS_CODE_OPTIONS_URL = '/api/v1/master/products/goods-codes'
export const STYLE_NUMBER_OPTIONS_URL = '/api/v1/master/products/style-numbers'

/** HEAD(문서) 조회. 서버가 API_FLAG=false + STATUS(WAIT/PROGRESS/CHECK) 를 강제하고 ordNo ASC 로 정렬해 준다. */
export const getErpDocumentSummaries = (params: GetErpDocumentsReq) =>
  request.get<ApiResponseV2<{ items: ErpDocumentSummary[] }>>(buildErpDocumentSummariesUrl(params))

/**
 * ITEM(품목) 조회. 무필터 호출은 BE 가 400 으로 막는다(prod 800만 행 힙 보호) —
 * 화면은 HEAD 선택 문서의 `ordNo` 를 항상 실어 호출하므로 이 경로에 도달하지 않는다.
 */
export const getErpDocumentItems = (params: GetErpDocumentsReq) =>
  request.get<ApiResponseV2<{ items: ErpDocumentItem[] }>>(buildErpDocumentsUrl(params))

/** 저장 — HEAD/ITEM 편집분 일괄 반영. actor 는 서버가 인증 컨텍스트에서 얻는다(body 에 없음). */
export const saveErpDocuments = (body: SaveErpDocumentsReq) =>
  request.post<ApiResponseV2<SaveErpDocumentsRes>, SaveErpDocumentsReq>(ERP_DOCUMENTS_SAVE_URL, body)

/** 확정 — 서버가 07:30~08:10 확정을 차단한다(IllegalArgumentException, meta.message 로 안내). */
export const confirmStockTransfer = (body: ConfirmStockTransferReq) =>
  request.post<ApiResponseV2<{ items: string[] }>, ConfirmStockTransferReq>(CONFIRM_STOCK_TRANSFER_URL, body)

/** 삭제(auth0 전용 화면 기능) — 문서번호 최대 100건, reason 은 legacy byte 15~90B. */
export const deleteErpDocuments = (body: DeleteErpDocumentsReq) =>
  request.post<ApiResponseV2<DeleteErpDocumentsRes>, DeleteErpDocumentsReq>(ERP_DOCUMENTS_DELETE_URL, body)

/** 삭제이력(auth0 전용) — 최근 30일, 서버가 인증 사용자 화주로 스코핑. EST01 계열 카테고리는 'STO'(1.0 removeSto 기록값). */
export const getRemoveLogs = (category: string) =>
  request.get<ApiResponseV2<{ items: DocumentRemoveLog[] }>>(`${REMOVE_LOGS_URL}?${toRepeatQuery({ category })}`)

/** 상품코드 서치헬퍼 목록 — 무페이징 distinct. 화주로 범위를 좁혀 조회한다. */
export const getGoodsCodeOptions = (params: GetGoodsCodeOptionsReq) =>
  request.get<ApiResponseV2<{ items: GoodsCodeOption[] }>>(`${GOODS_CODE_OPTIONS_URL}?${toRepeatQuery(params)}`)

/**
 * 스타일넘버 서치헬퍼 목록. (LOGISPRD-14184 — 1.0 `STL_NO_SearchForm` 대체)
 *
 * master_item distinct (스타일번호·브랜드명·상품코드), 스타일번호 오름차순, 최대 1,000행. 0건은 HTTP 200 + 빈 목록이다.
 * 브랜드명은 부분일치로 서버가 거르고, 스타일번호는 BE 가 정확일치라 화면이 받은 목록을 부분일치로 거른다.
 */
export const getStyleNumberOptions = (params: GetStyleNumberOptionsReq) =>
  request.get<ApiResponseV2<{ items: StyleNumberOptionItem[] }>>(`${STYLE_NUMBER_OPTIONS_URL}?${toRepeatQuery(params)}`)
